// useGoogleMapsLoader.js
// Loads the Google Maps JS API using the recommended `loading=async` pattern.
// Module-level state ensures the <script> tag is injected exactly once
// regardless of how many components call this hook.
//
// With no runtime `googleMapsApiKey` (the normal air-gapped case) the loader is
// inactive: it never injects the script and reports no error, so address
// fields are plain text inputs. `isInactive` tells forms to use the plain
// placeholder; `loadError` is reserved for a real load failure with a key.

import { useState, useEffect } from 'react';
import { REACT_APP_GOOGLE_MAPS_API_KEY } from "../config";

let loadState = 'idle'; // 'idle' | 'loading' | 'loaded' | 'error'
const listeners = new Set();

function broadcast(newState) {
  loadState = newState;
  listeners.forEach((fn) => fn(newState));
}

export const useGoogleMapsLoader = () => {
  const isInactive = !REACT_APP_GOOGLE_MAPS_API_KEY;
  const [isLoaded, setIsLoaded] = useState(loadState === 'loaded');
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (isInactive) return;
    if (loadState === 'loaded') { setIsLoaded(true); return; }
    if (loadState === 'error') { setLoadError('Address autocomplete unavailable.'); return; }

    const apiKey = REACT_APP_GOOGLE_MAPS_API_KEY;

    const onStateChange = (state) => {
      if (state === 'loaded') setIsLoaded(true);
      if (state === 'error') setLoadError('Address autocomplete unavailable.');
    };
    listeners.add(onStateChange);

    if (loadState === 'idle') {
      loadState = 'loading';

      window.__onGoogleMapsLoad = () => {
        broadcast('loaded');
        delete window.__onGoogleMapsLoad;
      };

      const script = document.createElement('script');
      // loading=async: recommended pattern per Google (eliminates the perf warning).
      // No &libraries= in the URL — we use google.maps.importLibrary() instead,
      // which is the correct pairing with loading=async.
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&callback=__onGoogleMapsLoad`;
      script.async = true;
      script.defer = true;
      script.onerror = () => broadcast('error');
      document.head.appendChild(script);
    }

    return () => listeners.delete(onStateChange);
  }, [isInactive]);

  return { isLoaded, loadError, isInactive };
};
